import React, { useState, useEffect } from 'react';
import { jobService } from '../../services/job.service';
import { Plus, X } from 'lucide-react';

const STORAGE_KEY = 'jobDescription';
const DEFAULT_PLATFORM_ICONS = {
  linkedin: (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
  ),
  indeed: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
    </svg>
  ),
  upwork: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
    </svg>
  )
};

const MediaContentCreator = ({ jobId, onSave, onCancel, initialContent }) => {
  const [formData, setFormData] = useState({
    jobDescription: '',
    selectedPlatform: ''
  });
  const [platforms, setPlatforms] = useState([]);
  const [content, setContent] = useState(initialContent || {});
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [newPlatformName, setNewPlatformName] = useState('');
  const [showAddPlatform, setShowAddPlatform] = useState(false);

  // Default icon for platforms without custom icons
  const DefaultIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );

  useEffect(() => {
    // Load platforms from localStorage or set defaults
    const savedPlatforms = localStorage.getItem('platforms');
    if (savedPlatforms) {
      setPlatforms(JSON.parse(savedPlatforms));
    } else {
      const defaultPlatforms = [
        { id: 'linkedin', name: 'LinkedIn' },
        { id: 'indeed', name: 'Indeed' },
        { id: 'upwork', name: 'Upwork' }
      ];
      setPlatforms(defaultPlatforms);
      localStorage.setItem('platforms', JSON.stringify(defaultPlatforms));
    }
  }, []);

  const handleAddPlatform = () => {
    if (!newPlatformName.trim()) return;

    const platformId = newPlatformName.toLowerCase().replace(/\s+/g, '-');
    const newPlatform = {
      id: platformId,
      name: newPlatformName.trim()
    };

    setPlatforms(prev => {
      const updated = [...prev, newPlatform];
      localStorage.setItem('platforms', JSON.stringify(updated));
      return updated;
    });

    setNewPlatformName('');
    setShowAddPlatform(false);
  };

  const handleRemovePlatform = (platformId) => {
    setPlatforms(prev => {
      const updated = prev.filter(p => p.id !== platformId);
      localStorage.setItem('platforms', JSON.stringify(updated));
      return updated;
    });

    if (formData.selectedPlatform === platformId) {
      setFormData(prev => ({ ...prev, selectedPlatform: '' }));
    }
  };

  const getPlatformIcon = (platformId) => {
    return DEFAULT_PLATFORM_ICONS[platformId] || <DefaultIcon />;
  };

  const handleInputChange = (e) => {
    setFormData(prev => ({
      ...prev,
      jobDescription: e.target.value
    }));
  };

  const handlePlatformSelect = (platformId) => {
    setFormData(prev => ({
      ...prev,
      selectedPlatform: platformId
    }));
  };

  const handleGenerate = async () => {
    if (!formData.jobDescription.trim() || !formData.selectedPlatform) {
      setError('Please select a platform and ensure job description is not empty');
      return;
    }

    try {
      setIsGenerating(true);
      setError(''); // Clear any previous errors
      const user = JSON.parse(localStorage.getItem('user'));
      const response = await jobService.generateMediaContent(
        jobId,
        formData.selectedPlatform,
        formData.jobDescription,
        user.id
      );
      
      // Check if the response contains an error
      if (response.data?.error) {
        let errorMessage = response.data.message;
        
        // Handle specific error codes
        if (response.data.code === 'INSUFFICIENT_TOKENS') {
          errorMessage = `Insufficient tokens. Required: ${response.data.details?.required}, Available: ${response.data.details?.available}`;
        } else if (response.data.code === 'SERVICE_UNAVAILABLE') {
          errorMessage = 'AI service is temporarily unavailable. Please try again in a few minutes.';
        } else if (response.data.code === 'RATE_LIMIT_EXCEEDED') {
          errorMessage = 'Rate limit exceeded. Please try again in a few minutes.';
        } else if (response.data.code === 'INVALID_REQUEST') {
          errorMessage = 'Invalid request. Please check your input and try again.';
        } else if (response.data.code === 'MEDIA_PLAN_GENERATION_ERROR') {
          errorMessage = response.data.message || 'Failed to generate media plan content. Please try again.';
        }
        
        setError(errorMessage);
        return;
      }
      
      // Handle new response format with options array
      if (response.options && Array.isArray(response.options)) {
        const contentData = response.options;
        
        const newContent = {
          ...content,
          [formData.selectedPlatform]: {
            content: contentData,
            hashtags: response.suggestedHashtags || [],
            keywords: response.suggestedKeywords || []
          }
        };
        setContent(newContent);
        
        await onSave(formData.selectedPlatform, contentData);
        onCancel();
      } else if (response.content) {
        // Handle both array format (new) and string format (legacy)
        const contentData = Array.isArray(response.content) 
          ? response.content 
          : [response.content];
        
        const newContent = {
          ...content,
          [formData.selectedPlatform]: {
            content: contentData,
            hashtags: response.suggestedHashtags || [],
            keywords: response.suggestedKeywords || []
          }
        };
        setContent(newContent);
        
        await onSave(formData.selectedPlatform, contentData);
        onCancel();
      } else {
        // Log the response for debugging
        console.error('Unexpected response format:', response);
        console.error('Response data:', response.data);
        
        setError('Failed to generate content: Invalid response format from AI service');
      }
    } catch (err) {
      // Handle specific error responses
      if (err.response?.data?.code === 'INSUFFICIENT_TOKENS') {
        const details = err.response.data.details;
        setError(
          `Insufficient tokens. You need ${details.required} tokens but have ${details.available} tokens available. Please upgrade your plan.`
        );
      } else if (err.response?.data?.code === 'TOKEN_EXPIRED') {
        setError('Your tokens have expired. Please renew your subscription.');
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Failed to generate content: ' + (err.message || 'Unknown error'));
      }
      console.error(err);
    } finally {
      setIsGenerating(false);
    }
  };

  useEffect(() => {
    if (jobId) {
      const cachedData = localStorage.getItem(`${STORAGE_KEY}_${jobId}`);
      if (cachedData) {
        const parsedData = JSON.parse(cachedData);
        const formattedJD = `${parsedData.generatedContent?.overview || parsedData.jobDescription?.overview || ''}`.trim();

        setFormData(prev => ({
          ...prev,
          jobDescription: formattedJD
        }));
      }
    }
  }, [jobId]);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold mb-2">
          <span className="text-orange-500">Hello,</span> I'm HRgpt!
        </h1>
      </div>

      {/* Steps */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="bg-gradient-to-r from-orange-500 to-yellow-500 p-6 rounded-xl text-white">
          <h2 className="text-lg font-semibold mb-2">01 Enter Job Description</h2>
          <p className="text-sm opacity-90">
            Enter Job description or paste from the previous step
          </p>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800 mb-2">02 Get custom plans</h2>
          <p className="text-sm text-gray-600">
            HRgpt will generate a custom tailored media plan & content that suits your organization
          </p>
        </div>
      </div>

      {/* Main Form */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
        {error && (
          <div className="mb-6">
            <div className="bg-red-50 border-l-4 border-red-500 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">
                    {error}
                  </p>
               
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Job Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Job Description
          </label>
          <div className="relative">
            <textarea
              value={formData.jobDescription}
              onChange={handleInputChange}
              rows={4}
              disabled={true}
              className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
              placeholder="Enter or paste Job description"
            />
            <div className="absolute right-1 top-2 flex gap-2">
              {/* Clear button */}
              {formData.jobDescription && (
                <button 
                  onClick={() => setFormData(prev => ({ ...prev, jobDescription: '' }))}
                  className="p-2 text-gray-400 hover:text-gray-600"
                  title="Clear text"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              {/* Paste button */}
              <button 
                onClick={() => navigator.clipboard.readText().then(text => setFormData(prev => ({ ...prev, jobDescription: text })))}
                className="p-2 text-gray-400 hover:text-gray-600"
                title="Paste from clipboard"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Platform Selection */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Select Target Platform
              </label>
              <span className="text-xs text-red-500">*Required</span>
            </div>
            <button
              onClick={() => setShowAddPlatform(true)}
              className="flex items-center gap-1 text-orange-500 hover:text-orange-600"
            >
              <Plus className="w-4 h-4" />
              <span>Add Platform</span>
            </button>
          </div>

          {showAddPlatform && (
            <div className="flex gap-2">
              <input
                type="text"
                value={newPlatformName}
                onChange={(e) => setNewPlatformName(e.target.value)}
                placeholder="Enter platform name"
                className="flex-1 px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-orange-400"
              />
              <button
                onClick={handleAddPlatform}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
              >
                Add
              </button>
              <button
                onClick={() => setShowAddPlatform(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {platforms.map(platform => (
              <div key={platform.id} className="relative group">
                <button
                  onClick={() => handlePlatformSelect(platform.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${
                    formData.selectedPlatform === platform.id
                      ? 'bg-orange-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {getPlatformIcon(platform.id)}
                  <span>{platform.name}</span>
                </button>
                {/* Remove platform button */}
                <button
                  onClick={() => handleRemovePlatform(platform.id)}
                  className="absolute -top-2 -right-2 hidden group-hover:flex h-5 w-5 items-center justify-center bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Generated Content */}
        {content[formData.selectedPlatform] && (
          <div className="space-y-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Generated Content
            </label>
            <textarea
              value={content[formData.selectedPlatform].content}
              onChange={(e) => setContent(prev => ({
                ...prev,
                [formData.selectedPlatform]: {
                  ...prev[formData.selectedPlatform],
                  content: e.target.value
                }
              }))}
              rows={8}
              className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
              placeholder={`${formData.selectedPlatform} content...`}
            />

            {/* Hashtags */}
            {content[formData.selectedPlatform].hashtags?.length > 0 && (
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Suggested Hashtags
                </label>
                <div className="flex flex-wrap gap-2">
                  {content[formData.selectedPlatform].hashtags.map((hashtag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1.5 bg-orange-50 text-orange-600 rounded-full text-sm font-medium border border-orange-200 hover:bg-orange-100 transition-colors cursor-pointer"
                    >
                      {hashtag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Keywords */}
            {content[formData.selectedPlatform].keywords?.length > 0 && (
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Suggested Keywords
                </label>
                <div className="flex flex-wrap gap-2">
                  {content[formData.selectedPlatform].keywords.map((keyword, index) => (
                    <div
                      key={index}
                      className="group relative"
                    >
                      <span
                        className="inline-flex items-center px-3 py-1.5 bg-gray-50 text-gray-700 rounded-full text-sm font-medium border border-gray-200 hover:bg-gray-100 transition-colors cursor-pointer"
                      >
                        {keyword}
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(keyword);
                            // You could add a toast notification here
                          }}
                          className="ml-2 text-gray-400 hover:text-gray-600"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                          </svg>
                        </button>
                      </span>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity absolute -top-8 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap">
                        Click to copy
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            disabled={isGenerating || !formData.jobDescription || !formData.selectedPlatform}
            className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isGenerating ? (
              <>
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span>Generating...</span>
              </>
            ) : (
              <span>{!formData.selectedPlatform ? 'Select a Platform' : 'Generate'}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MediaContentCreator;